import { Layers, Pencil, Plus, Power, Search, Upload } from 'lucide-react';
import { useMemo, useState } from 'react';
import { PERMISSION } from '@obraplan/core/domain/access/permissions';
import { normalizeName } from '@obraplan/core/domain/shared/naming';
import type { Discipline } from '@obraplan/core/domain/structure/discipline';
import type { DisciplineColumnMapping } from '@obraplan/core/domain/structure/discipline-import';
import type { DisciplineImportPreview } from '@obraplan/core/application/use-cases/structure/discipline-catalog-use-cases';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableWrapper,
} from '@/components/ui/table';
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/states';
import { DisciplineFormDialog } from '@/features/structure/DisciplineFormDialog';
import { DisciplineImportDialog } from '@/features/structure/DisciplineImportDialog';
import {
  useContainer,
  usePermissionGlobally,
  useSessionState,
} from '@/features/session/use-session';
import { useCommand } from '@/lib/use-command';
import { useLive } from '@/lib/use-live';

/**
 * Catálogo de disciplinas — global, compartilhado por todas as obras (RN-007), por isso em
 * Configurações. Grafias divergentes viram disciplinas diferentes na árvore.
 */
export function DisciplinesPage() {
  const container = useContainer();
  const { session } = useSessionState();
  const canManage = usePermissionGlobally(PERMISSION.STRUCTURE_MANAGE);

  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Discipline | undefined>(undefined);
  const [importOpen, setImportOpen] = useState(false);
  const [preview, setPreview] = useState<DisciplineImportPreview | undefined>(undefined);
  // Os bytes ficam aqui para que trocar o mapeamento não exija reabrir o arquivo.
  const [fileBytes, setFileBytes] = useState<{ bytes: Uint8Array; name: string } | undefined>(
    undefined,
  );

  const catalog = useLive(
    () =>
      session === undefined
        ? Promise.resolve(undefined)
        : container.listDisciplineCatalog.execute(session),
    [session?.userId],
  );

  const entries = useMemo(
    () => (catalog !== undefined && catalog.ok ? catalog.value.entries : []),
    [catalog],
  );

  const filtered = useMemo(() => {
    const term = normalizeName(search);
    if (term === '') return entries;
    return entries.filter(
      (entry) =>
        normalizeName(entry.discipline.name).includes(term) ||
        normalizeName(entry.discipline.code ?? '').includes(term),
    );
  }, [entries, search]);

  const closeForm = (): void => {
    setFormOpen(false);
    setEditing(undefined);
  };

  const [createDiscipline, createState] = useCommand(
    (input: { name: string; code?: string; color: string; unitOfMeasureId?: string }) =>
      container.createDiscipline.execute(input, session!),
    { successMessage: 'Disciplina criada.', silent: true, onSuccess: closeForm },
  );

  const [updateDiscipline, updateState] = useCommand(
    (input: {
      disciplineId: string;
      name: string;
      code?: string;
      color: string;
      unitOfMeasureId?: string;
    }) => container.updateDiscipline.execute(input, session!),
    { successMessage: 'Disciplina atualizada.', silent: true, onSuccess: closeForm },
  );

  const [toggleActive] = useCommand(
    (input: { disciplineId: string; isActive: boolean }) =>
      container.setDisciplineActive.execute(input, session!),
    {
      successMessage: (discipline) =>
        discipline.isActive ? 'Disciplina reativada.' : 'Disciplina desativada.',
    },
  );

  const [runPreview, previewState] = useCommand(
    (input: { bytes: Uint8Array; fileName: string; mapping?: DisciplineColumnMapping }) =>
      container.previewDisciplineImport.execute(input, session!),
    { silent: true, onSuccess: (value) => setPreview(value) },
  );

  const closeImport = (): void => {
    setImportOpen(false);
    setPreview(undefined);
    setFileBytes(undefined);
  };

  const [confirmImport, confirmState] = useCommand(
    (updateExisting: boolean) =>
      container.confirmDisciplineImport.execute(
        { rows: preview?.rows ?? [], updateExisting, fileName: preview?.fileName ?? '' },
        session!,
      ),
    {
      successMessage: (result) =>
        `${String(result.created)} disciplinas criadas · ${String(result.updated)} atualizadas.`,
      silent: true,
      onSuccess: closeImport,
    },
  );

  const handleFile = (file: File): void => {
    void file.arrayBuffer().then((buffer) => {
      const bytes = new Uint8Array(buffer);
      setFileBytes({ bytes, name: file.name });
      void runPreview({ bytes, fileName: file.name });
    });
  };

  const handleRemap = (mapping: DisciplineColumnMapping): void => {
    if (fileBytes === undefined) return;
    void runPreview({ bytes: fileBytes.bytes, fileName: fileBytes.name, mapping });
  };

  const submitForm = (input: { name: string; code: string | undefined; color: string }): void => {
    const payload = {
      name: input.name,
      color: input.color,
      ...(input.code === undefined ? {} : { code: input.code }),
    };
    if (editing === undefined) void createDiscipline(payload);
    else void updateDiscipline({ disciplineId: editing.id, ...payload });
  };

  if (catalog === undefined) return <LoadingState label="Carregando as disciplinas…" />;
  if (!catalog.ok) return <ErrorState message={catalog.error.message} code={catalog.error.code} />;

  const activeCount = entries.filter((entry) => entry.discipline.isActive).length;

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Disciplinas</h1>
          <p className="text-(--color-content-muted)">
            Catálogo único, igual para todas as obras. Onde cada disciplina é aplicada é decidido na
            estrutura da obra.
          </p>
        </div>
        {canManage && (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                setEditing(undefined);
                setFormOpen(true);
              }}
            >
              <Plus aria-hidden="true" />
              Nova disciplina
            </Button>
            <Button variant="primary" onClick={() => setImportOpen(true)}>
              <Upload aria-hidden="true" />
              Importar planilha
            </Button>
          </div>
        )}
      </header>

      <Card>
        <CardHeader className="flex-row items-center justify-between gap-3">
          <div>
            <CardTitle>
              {activeCount} disciplinas ativas
              {entries.length > activeCount && ` · ${entries.length - activeCount} desativadas`}
            </CardTitle>
            <CardDescription>
              O nome identifica a disciplina; desativar tira das opções sem apagar as frentes.
            </CardDescription>
          </div>
          <div className="relative flex items-center">
            <Search
              className="pointer-events-none absolute left-2.5 size-4 text-(--color-content-muted)"
              aria-hidden="true"
            />
            <Input
              aria-label="Buscar disciplina"
              className="w-56 pl-8"
              placeholder="Buscar por nome, código…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <EmptyState
              title={entries.length === 0 ? 'Catálogo vazio' : 'Nenhuma disciplina encontrada'}
              description={
                entries.length === 0
                  ? 'Suba a planilha com a lista de serviços da obra ou cadastre uma disciplina.'
                  : 'Nenhuma disciplina corresponde à busca.'
              }
              icon={<Layers className="size-8" />}
              {...(canManage && entries.length === 0
                ? { action: { label: 'Importar planilha', onClick: () => setImportOpen(true) } }
                : {})}
            />
          ) : (
            <TableWrapper>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <span className="sr-only">Cor</span>
                    </TableHead>
                    <TableHead>Disciplina</TableHead>
                    <TableHead>Código</TableHead>
                    <TableHead>Frentes</TableHead>
                    <TableHead>Situação</TableHead>
                    {canManage && (
                      <TableHead className="text-right">
                        <span className="sr-only">Ações</span>
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(({ discipline, activityCount }) => (
                    <TableRow key={discipline.id}>
                      <TableCell>
                        <span
                          aria-hidden="true"
                          className="block size-4 rounded-full border border-(--color-border-subtle)"
                          style={{ backgroundColor: discipline.color ?? 'transparent' }}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{discipline.name}</TableCell>
                      <TableCell className="font-mono text-xs">{discipline.code ?? '—'}</TableCell>
                      <TableCell className="tabular text-(--color-content-muted)">
                        {activityCount}
                      </TableCell>
                      <TableCell>
                        {discipline.isActive ? (
                          <Badge tone="ok">Ativa</Badge>
                        ) : (
                          <Badge tone="neutral">Desativada</Badge>
                        )}
                      </TableCell>
                      {canManage && (
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              aria-label={`Editar ${discipline.name}`}
                              onClick={() => {
                                setEditing(discipline);
                                setFormOpen(true);
                              }}
                            >
                              <Pencil aria-hidden="true" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              aria-label={
                                discipline.isActive
                                  ? `Desativar ${discipline.name}`
                                  : `Reativar ${discipline.name}`
                              }
                              onClick={() =>
                                void toggleActive({
                                  disciplineId: discipline.id,
                                  isActive: !discipline.isActive,
                                })
                              }
                            >
                              <Power aria-hidden="true" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableWrapper>
          )}
        </CardContent>
      </Card>

      <DisciplineFormDialog
        open={formOpen}
        onOpenChange={(open) => {
          if (!open) closeForm();
          else setFormOpen(true);
        }}
        discipline={editing}
        onSubmit={submitForm}
        isPending={createState.isPending || updateState.isPending}
        errorMessage={
          editing === undefined ? createState.error?.message : updateState.error?.message
        }
      />

      <DisciplineImportDialog
        open={importOpen}
        onOpenChange={(open) => {
          if (!open) closeImport();
          else setImportOpen(true);
        }}
        preview={preview}
        onFile={handleFile}
        onRemap={handleRemap}
        onConfirm={(updateExisting) => void confirmImport(updateExisting)}
        isPending={previewState.isPending || confirmState.isPending}
        errorMessage={previewState.error?.message ?? confirmState.error?.message}
      />
    </div>
  );
}
